import React, { useState, useEffect } from 'react';
import { 
  Bell, Settings, Users, Shield, Send, RefreshCw,
  Check, X, AlertCircle, Clock, ChevronDown, ChevronRight,
  Search, Plus, Trash2, Eye, Edit2, Save, RotateCcw, MessageSquare,
  FileText, Copy
} from 'lucide-react';
import { api } from '../api/client';

// Microsoft Teams icon component
const TeamsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.4 9.6c1.32 0 2.4-1.08 2.4-2.4S21.72 4.8 20.4 4.8 18 5.88 18 7.2s1.08 2.4 2.4 2.4zM14.4 4.8c1.98 0 3.6-1.62 3.6-3.6S16.38-2.4 14.4-2.4 10.8-.78 10.8 1.2s1.62 3.6 3.6 3.6zm0 1.2c-2.94 0-8.4 1.5-8.4 4.44v1.56h16.8v-1.56c0-2.94-5.46-4.44-8.4-4.44zm6.6 4.8h3c.84 0 1.5-.66 1.5-1.5v-6c0-.84-.66-1.5-1.5-1.5h-4.8v.24c1.26.78 2.1 2.16 2.1 3.72 0 .96-.3 1.86-.78 2.58.54.42 1.2.66 1.98.66V12h-1.5z"/>
  </svg>
);

const ROLES = [
  { key: 'sysadmin', label: 'System Admin', color: 'red' },
  { key: 'pmo_power', label: 'PMO Power User', color: 'purple' },
  { key: 'program_admin', label: 'Program Admin', color: 'blue' },
  { key: 'pm', label: 'Project Manager', color: 'green' },
  { key: 'viewer', label: 'Viewer', color: 'gray' }
];

const CATEGORIES = [
  'health', 'blockers', 'risks', 'issues', 'dependencies',
  'decisions', 'milestones', 'overdue', 'upcoming', 'changes'
];

const AdminNotificationConsole = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Global Config
  const [config, setConfig] = useState({
    enabled: true,
    whatsapp_api_url: '',
    whatsapp_api_token: '',
    email_sender: '',
    default_timezone: 'UTC'
  });

  // Teams Config
  const [teamsConfig, setTeamsConfig] = useState({
    teams_enabled: false,
    teams_mode: 'channel_webhook',
    teams_webhooks: [],
    teams_graph_configured: false,
    teams_quiet_hours: null,
    teams_max_per_channel_per_day: 1
  });
  const [newWebhook, setNewWebhook] = useState({ url: '', label: '' });
  const [testingWebhook, setTestingWebhook] = useState(null);
  
  // Teams Graph API v1.5
  const [graphTargets, setGraphTargets] = useState([]);
  const [graphStatus, setGraphStatus] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamChannels, setTeamChannels] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [newGraphTarget, setNewGraphTarget] = useState({ team_id: '', channel_id: '', label: '', target_type: 'channel' });
  const [testingGraph, setTestingGraph] = useState(null);

  // Role Defaults
  const [roleDefaults, setRoleDefaults] = useState([]);
  const [editingRole, setEditingRole] = useState(null);

  // User Overrides
  const [userOverrides, setUserOverrides] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [editingOverride, setEditingOverride] = useState(null);

  // Outbox
  const [outboxEntries, setOutboxEntries] = useState([]);
  const [outboxStats, setOutboxStats] = useState({});
  const [outboxFilter, setOutboxFilter] = useState(null);

  // Signal Templates
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [cloneName, setCloneName] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'config') {
        const [configRes, teamsRes] = await Promise.all([
          api.getNotificationConfig(),
          api.getTeamsConfig().catch(() => ({ data: {} }))
        ]);
        setConfig(configRes.data.config || {});
        setTeamsConfig(teamsRes.data || {});
      } else if (activeTab === 'teams') {
        const [teamsRes, graphTargetsRes, graphStatusRes] = await Promise.all([
          api.getTeamsConfig(),
          api.getTeamsGraphTargets(),
          api.getTeamsGraphStatus()
        ]);
        setTeamsConfig(teamsRes.data || {});
        setGraphTargets(graphTargetsRes.data.targets || []);
        setGraphStatus(graphStatusRes.data || {});
      } else if (activeTab === 'roles') {
        const res = await api.getNotificationRoleDefaults();
        setRoleDefaults(res.data.role_defaults || []);
      } else if (activeTab === 'overrides') {
        const [overridesRes, usersRes] = await Promise.all([
          api.getNotificationUserOverrides(),
          api.getAdminUsers()
        ]);
        setUserOverrides(overridesRes.data.overrides || []);
        setUsers(usersRes.data || []);
      } else if (activeTab === 'outbox') {
        const res = await api.getNotificationOutbox(outboxFilter);
        setOutboxEntries(res.data.entries || []);
        setOutboxStats(res.data.stats || {});
      } else if (activeTab === 'templates') {
        const res = await api.getSignalTemplates(true);
        setTemplates(res.data.templates || []);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      await api.saveNotificationConfig(config);
      setSuccess('Configuration saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveRoleDefault = async (roleData) => {
    try {
      setSaving(true);
      await api.saveNotificationRoleDefault(roleData);
      setSuccess(`Saved defaults for ${roleData.role}`);
      setEditingRole(null);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveUserOverride = async (override) => {
    try {
      setSaving(true);
      await api.saveNotificationUserOverride(override);
      setSuccess('Override saved');
      setEditingOverride(null);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteOverride = async (userId) => {
    if (!window.confirm('Delete this override?')) return;
    try {
      await api.deleteNotificationUserOverride(userId);
      setSuccess('Override deleted');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const retryNotification = async (entryId) => {
    try {
      await api.retryNotification(entryId);
      setSuccess('Notification re-queued');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to retry');
    }
  };

  const renderConfigTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-[var(--clr-text-primary)]">Global Configuration</h3>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
          data-testid="save-config-btn"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>

      {/* Global Enable */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-[var(--clr-text-primary)]">Daily Signal™ System</p>
            <p className="text-sm text-[var(--clr-text-muted)]">Enable or disable for all users</p>
          </div>
          <button
            onClick={() => setConfig({...config, enabled: !config.enabled})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              config.enabled ? 'bg-[var(--clr-brand-primary)]' : 'bg-gray-200'
            }`}
            data-testid="global-enable-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config.enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* WhatsApp Config */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4 space-y-4">
        <h4 className="font-medium text-[var(--clr-text-primary)]">WhatsApp Business API</h4>
        <div>
          <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">API URL</label>
          <input
            type="url"
            value={config.whatsapp_api_url || ''}
            onChange={(e) => setConfig({...config, whatsapp_api_url: e.target.value})}
            placeholder="https://api.whatsapp.com/v1/..."
            className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
            data-testid="whatsapp-api-url"
          />
        </div>
        <div>
          <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">API Token</label>
          <input
            type="password"
            value={config.whatsapp_api_token || ''}
            onChange={(e) => setConfig({...config, whatsapp_api_token: e.target.value})}
            placeholder="••••••••••••••••"
            className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
            data-testid="whatsapp-api-token"
          />
        </div>
      </div>

      {/* Email Config */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
        <h4 className="font-medium text-[var(--clr-text-primary)] mb-4">Email Fallback</h4>
        <div>
          <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Sender Email</label>
          <input
            type="email"
            value={config.email_sender || ''}
            onChange={(e) => setConfig({...config, email_sender: e.target.value})}
            placeholder="noreply@scopeops.ai"
            className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
            data-testid="email-sender"
          />
        </div>
      </div>

      {/* Timezone */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
        <h4 className="font-medium text-[var(--clr-text-primary)] mb-4">Default Timezone</h4>
        <select
          value={config.default_timezone || 'UTC'}
          onChange={(e) => setConfig({...config, default_timezone: e.target.value})}
          className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
          data-testid="default-timezone"
        >
          <option value="UTC">UTC</option>
          <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
          <option value="America/New_York">America/New York (EST)</option>
          <option value="America/Los_Angeles">America/Los Angeles (PST)</option>
          <option value="Europe/London">Europe/London (GMT)</option>
          <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
        </select>
      </div>
    </div>
  );

  const renderRolesTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-[var(--clr-text-primary)]">Role Default Policies</h3>
      <p className="text-sm text-[var(--clr-text-muted)]">
        Configure default notification settings for each role. Users can customize within these limits.
      </p>

      <div className="space-y-3">
        {ROLES.map(role => {
          const roleData = roleDefaults.find(r => r.role === role.key) || {
            role: role.key,
            frequency: 'daily',
            days_of_week: [1, 2, 3, 4, 5],
            times: ['08:00'],
            categories: ['health', 'blockers', 'risks'],
            format: 'short',
            scope_type: 'mine',
            max_per_day: 3,
            allow_custom_times: false,
            allow_detailed: false,
            include_cost_fields: false
          };

          const isEditing = editingRole === role.key;

          return (
            <div
              key={role.key}
              className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] overflow-hidden"
            >
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-[var(--clr-surface-hover)]"
                onClick={() => setEditingRole(isEditing ? null : role.key)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full bg-${role.color}-500`}
                       style={{backgroundColor: role.color === 'red' ? '#ef4444' : role.color === 'purple' ? '#8b5cf6' : role.color === 'blue' ? '#3b82f6' : role.color === 'green' ? '#22c55e' : '#6b7280'}} />
                  <span className="font-medium text-[var(--clr-text-primary)]">{role.label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${isEditing ? 'rotate-180' : ''}`} />
              </div>

              {isEditing && (
                <div className="border-t border-[var(--clr-border-light)] p-4 space-y-4 bg-[var(--clr-surface-subtle)]">
                  <RoleDefaultEditor
                    data={roleData}
                    onSave={(data) => saveRoleDefault({...data, role: role.key})}
                    onCancel={() => setEditingRole(null)}
                    saving={saving}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderOverridesTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-[var(--clr-text-primary)]">User Overrides</h3>
          <p className="text-sm text-[var(--clr-text-muted)]">
            Grant or restrict notification privileges for specific users
          </p>
        </div>
        <button
          onClick={() => setEditingOverride({
            user_id: '',
            frequency: null,
            times: null,
            categories: null,
            max_per_day: null,
            allow_custom_times: null,
            allow_detailed: null,
            include_cost_fields: null,
            reason: '',
            expires_at: null
          })}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)]"
          data-testid="add-override-btn"
        >
          <Plus className="w-4 h-4" />
          Add Override
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--clr-text-muted)]" />
        <input
          type="text"
          value={userSearchQuery}
          onChange={(e) => setUserSearchQuery(e.target.value)}
          placeholder="Search users..."
          className="w-full pl-10 pr-4 py-2 border border-[var(--clr-border-medium)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
          data-testid="search-users"
        />
      </div>

      {/* Overrides List */}
      <div className="space-y-2">
        {userOverrides
          .filter(o => {
            if (!userSearchQuery) return true;
            const user = users.find(u => u.id === o.user_id);
            return user?.email?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                   user?.name?.toLowerCase().includes(userSearchQuery.toLowerCase());
          })
          .map(override => {
            const user = users.find(u => u.id === override.user_id);
            return (
              <div
                key={override.user_id}
                className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[var(--clr-text-primary)]">
                      {user?.name || user?.email || override.user_id}
                    </p>
                    <p className="text-sm text-[var(--clr-text-muted)]">
                      {override.reason || 'No reason specified'}
                    </p>
                    {override.expires_at && (
                      <p className="text-xs text-orange-500">
                        Expires: {new Date(override.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingOverride(override)}
                      className="p-2 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteOverride(override.user_id)}
                      className="p-2 text-[var(--clr-text-muted)] hover:text-red-500 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Override Editor Modal */}
      {editingOverride && (
        <UserOverrideEditor
          data={editingOverride}
          users={users}
          onSave={saveUserOverride}
          onCancel={() => setEditingOverride(null)}
          saving={saving}
        />
      )}
    </div>
  );

  const renderOutboxTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-[var(--clr-text-primary)]">Notification Outbox</h3>
          <p className="text-sm text-[var(--clr-text-muted)]">
            Monitor sent messages and retry failed deliveries
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-3 py-2 text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)] border border-[var(--clr-border-medium)] rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { key: 'queued', label: 'Queued', color: 'blue' },
          { key: 'sent', label: 'Sent', color: 'green' },
          { key: 'failed', label: 'Failed', color: 'red' },
          { key: 'skipped', label: 'Skipped', color: 'gray' }
        ].map(stat => (
          <button
            key={stat.key}
            onClick={() => setOutboxFilter(outboxFilter === stat.key ? null : stat.key)}
            className={`p-4 rounded-lg border transition-all ${
              outboxFilter === stat.key
                ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] bg-opacity-5'
                : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
            }`}
          >
            <p className="text-2xl font-bold text-[var(--clr-text-primary)]">
              {outboxStats[stat.key] || 0}
            </p>
            <p className="text-sm text-[var(--clr-text-muted)]">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Entries */}
      <div className="space-y-2">
        {outboxEntries.map(entry => (
          <div
            key={entry.id}
            className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  entry.status === 'sent' ? 'bg-green-500' :
                  entry.status === 'failed' ? 'bg-red-500' :
                  entry.status === 'queued' ? 'bg-blue-500' : 'bg-gray-400'
                }`} />
                <div>
                  <p className="text-sm font-medium text-[var(--clr-text-primary)]">
                    {entry.user_id}
                  </p>
                  <p className="text-xs text-[var(--clr-text-muted)]">
                    {entry.channel} • {new Date(entry.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  entry.status === 'sent' ? 'bg-green-100 text-green-700' :
                  entry.status === 'failed' ? 'bg-red-100 text-red-700' :
                  entry.status === 'queued' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {entry.status}
                </span>
                {entry.status === 'failed' && (
                  <button
                    onClick={() => retryNotification(entry.id)}
                    className="p-2 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] rounded"
                    title="Retry"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {entry.error && (
              <p className="mt-2 text-xs text-red-500">{entry.error}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[var(--clr-brand-primary)] bg-opacity-10 flex items-center justify-center">
          <Bell className="w-5 h-5 text-[var(--clr-brand-primary)]" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-[var(--clr-text-primary)]">
            Daily Signal™ Administration
          </h2>
          <p className="text-sm text-[var(--clr-text-muted)]">
            Configure notification policies and monitor delivery
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[var(--clr-border-light)]">
        {[
          { key: 'config', label: 'Global Config', icon: Settings },
          { key: 'teams', label: 'Microsoft Teams', icon: MessageSquare },
          { key: 'templates', label: 'Templates', icon: FileText },
          { key: 'roles', label: 'Role Defaults', icon: Shield },
          { key: 'overrides', label: 'User Overrides', icon: Users },
          { key: 'outbox', label: 'Outbox Log', icon: Send }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-[var(--clr-brand-primary)] text-[var(--clr-brand-primary)]'
                : 'border-transparent text-[var(--clr-text-muted)] hover:text-[var(--clr-text-primary)]'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
        </div>
      ) : (
        <>
          {activeTab === 'config' && renderConfigTab()}
          {activeTab === 'teams' && renderTeamsTab()}
          {activeTab === 'templates' && renderTemplatesTab()}
          {activeTab === 'roles' && renderRolesTab()}
          {activeTab === 'overrides' && renderOverridesTab()}
          {activeTab === 'outbox' && renderOutboxTab()}
        </>
      )}
    </div>
  );

  // Teams Tab Renderer
  function renderTeamsTab() {
    const handleAddWebhook = async () => {
      if (!newWebhook.url || !newWebhook.label) {
        setError('Please enter both URL and label');
        return;
      }
      try {
        setSaving(true);
        await api.addTeamsWebhook(newWebhook);
        setNewWebhook({ url: '', label: '' });
        const res = await api.getTeamsConfig();
        setTeamsConfig(res.data || {});
        setSuccess('Webhook added successfully');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to add webhook');
      } finally {
        setSaving(false);
      }
    };

    const handleRemoveWebhook = async (url) => {
      if (!confirm('Remove this webhook?')) return;
      try {
        await api.removeTeamsWebhook(url);
        const res = await api.getTeamsConfig();
        setTeamsConfig(res.data || {});
        setSuccess('Webhook removed');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Failed to remove webhook');
      }
    };

    const handleTestWebhook = async (url) => {
      setTestingWebhook(url);
      try {
        const res = await api.testTeamsWebhook(url);
        if (res.data.success) {
          setSuccess('Test message sent to Teams channel!');
        } else {
          setError('Test failed: ' + (res.data.result?.error || 'Unknown error'));
        }
        setTimeout(() => setSuccess(null), 5000);
      } catch (err) {
        setError('Test failed: ' + (err.response?.data?.detail || err.message));
      } finally {
        setTestingWebhook(null);
      }
    };

    return (
      <div className="space-y-6">
        {/* Teams Enable Toggle */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-[var(--clr-text-primary)]">Microsoft Teams Integration</h3>
                <p className="text-sm text-[var(--clr-text-muted)]">Send Daily Signal™ notifications to Teams channels</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={teamsConfig.teams_enabled}
                onChange={async (e) => {
                  const newConfig = { ...config, teams_enabled: e.target.checked };
                  await api.saveNotificationConfig(newConfig);
                  setTeamsConfig(prev => ({ ...prev, teams_enabled: e.target.checked }));
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>

          {/* Mode Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">Delivery Mode</label>
            <select
              value={teamsConfig.teams_mode || 'channel_webhook'}
              onChange={(e) => setTeamsConfig(prev => ({ ...prev, teams_mode: e.target.value }))}
              className="w-full md:w-64 px-3 py-2 border rounded-lg bg-white"
              disabled={!teamsConfig.teams_enabled}
            >
              <option value="channel_webhook">Incoming Webhook (Recommended)</option>
              {teamsConfig.teams_graph_configured && (
                <>
                  <option value="channel_graph">Graph API - Channel</option>
                  <option value="group_chat">Graph API - Group Chat</option>
                  <option value="dm">Graph API - Direct Message</option>
                </>
              )}
            </select>
            {!teamsConfig.teams_graph_configured && (
              <p className="mt-1 text-xs text-amber-600">Graph API modes require Azure AD configuration</p>
            )}
          </div>

          {/* Throttling */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Max Messages/Channel/Day</label>
              <input
                type="number"
                value={teamsConfig.teams_max_per_channel_per_day || 1}
                onChange={(e) => setTeamsConfig(prev => ({ ...prev, teams_max_per_channel_per_day: parseInt(e.target.value) || 1 }))}
                className="w-24 px-3 py-2 border rounded-lg"
                min="1"
                max="10"
                disabled={!teamsConfig.teams_enabled}
              />
              <p className="mt-1 text-xs text-[var(--clr-text-muted)]">Limit non-critical messages</p>
            </div>
          </div>
        </div>

        {/* Webhook Targets */}
        {teamsConfig.teams_mode === 'channel_webhook' && (
          <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)] p-6">
            <h3 className="font-semibold text-[var(--clr-text-primary)] mb-4">Channel Webhooks</h3>
            <p className="text-sm text-[var(--clr-text-muted)] mb-4">
              Add Incoming Webhook URLs from your Teams channels. 
              <a href="https://docs.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook" 
                 target="_blank" rel="noopener noreferrer" 
                 className="text-[var(--clr-brand-primary)] hover:underline ml-1">
                Learn how to create webhooks
              </a>
            </p>

            {/* Add Webhook Form */}
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                placeholder="Channel label (e.g., PMO Alerts)"
                value={newWebhook.label}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, label: e.target.value }))}
                className="flex-1 max-w-xs px-3 py-2 border rounded-lg text-sm"
                data-testid="teams-webhook-label"
              />
              <input
                type="url"
                placeholder="https://outlook.office.com/webhook/..."
                value={newWebhook.url}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                data-testid="teams-webhook-url"
              />
              <button
                onClick={handleAddWebhook}
                disabled={saving || !newWebhook.url || !newWebhook.label}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                data-testid="add-teams-webhook-btn"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add
              </button>
            </div>

            {/* Webhook List */}
            <div className="space-y-2">
              {teamsConfig.teams_webhooks?.length === 0 && (
                <p className="text-sm text-[var(--clr-text-muted)] py-4 text-center border-2 border-dashed rounded-lg">
                  No webhooks configured yet
                </p>
              )}
              {teamsConfig.teams_webhooks?.map((webhook, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${webhook.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div>
                      <p className="font-medium text-sm">{webhook.label}</p>
                      <p className="text-xs text-[var(--clr-text-muted)] truncate max-w-md">
                        {webhook.url.substring(0, 60)}...
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTestWebhook(webhook.url)}
                      disabled={testingWebhook === webhook.url}
                      className="px-3 py-1 text-xs border border-purple-300 text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50"
                      data-testid={`test-webhook-${idx}`}
                    >
                      {testingWebhook === webhook.url ? (
                        <RefreshCw className="w-3 h-3 animate-spin inline mr-1" />
                      ) : (
                        <Send className="w-3 h-3 inline mr-1" />
                      )}
                      Test
                    </button>
                    <button
                      onClick={() => handleRemoveWebhook(webhook.url)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      data-testid={`remove-webhook-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Graph API Configuration Info */}
        {(teamsConfig.teams_mode === 'channel_graph' || teamsConfig.teams_mode === 'group_chat' || teamsConfig.teams_mode === 'dm') && (
          <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)] p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[var(--clr-text-primary)]">Graph API Configuration (v1.5)</h3>
                <p className="text-sm text-[var(--clr-text-muted)]">
                  Send messages to specific Teams channels, group chats, or user DMs
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                graphStatus?.token_valid 
                  ? 'bg-green-100 text-green-700' 
                  : graphStatus?.configured 
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
              }`}>
                {graphStatus?.token_valid ? 'Connected' : graphStatus?.configured ? 'Token Invalid' : 'Not Configured'}
              </div>
            </div>

            {/* Environment Variables Required */}
            {!graphStatus?.configured && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-2 font-medium">Required Environment Variables:</p>
                <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                  <li><code className="bg-amber-100 px-1 rounded">TEAMS_TENANT_ID</code> - Azure AD tenant ID {graphStatus?.tenant_id_set ? '✓' : '✗'}</li>
                  <li><code className="bg-amber-100 px-1 rounded">TEAMS_CLIENT_ID</code> - Azure AD app client ID {graphStatus?.client_id_set ? '✓' : '✗'}</li>
                  <li><code className="bg-amber-100 px-1 rounded">TEAMS_CLIENT_SECRET</code> - Azure AD app secret {graphStatus?.client_secret_set ? '✓' : '✗'}</li>
                </ul>
                <p className="text-xs text-amber-600 mt-2">
                  Configure these in your backend .env file and restart the server.
                </p>
              </div>
            )}

            {/* Graph API Targets Configuration */}
            {graphStatus?.configured && (
              <>
                {/* Add New Target Section */}
                <div className="border-t border-[var(--clr-border-light)] pt-6">
                  <h4 className="font-medium text-[var(--clr-text-primary)] mb-4">Add Delivery Target</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Target Type */}
                    <div>
                      <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Target Type</label>
                      <select
                        value={newGraphTarget.target_type}
                        onChange={e => setNewGraphTarget({ ...newGraphTarget, target_type: e.target.value, team_id: '', channel_id: '' })}
                        className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                        data-testid="graph-target-type"
                      >
                        <option value="channel">Team Channel</option>
                        <option value="dm">Direct Message (User Email)</option>
                      </select>
                    </div>

                    {/* Label */}
                    <div>
                      <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Label</label>
                      <input
                        type="text"
                        value={newGraphTarget.label}
                        onChange={e => setNewGraphTarget({ ...newGraphTarget, label: e.target.value })}
                        placeholder="e.g., Engineering Channel"
                        className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                        data-testid="graph-target-label"
                      />
                    </div>
                  </div>

                  {/* Team Channel Selection */}
                  {newGraphTarget.target_type === 'channel' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Select Team</label>
                        <div className="flex gap-2">
                          <select
                            value={newGraphTarget.team_id}
                            onChange={async (e) => {
                              const teamId = e.target.value;
                              const team = availableTeams.find(t => t.id === teamId);
                              setNewGraphTarget({ 
                                ...newGraphTarget, 
                                team_id: teamId, 
                                team_name: team?.name || '',
                                channel_id: '' 
                              });
                              if (teamId) {
                                setLoadingTeams(true);
                                try {
                                  const res = await api.listTeamChannels(teamId);
                                  setTeamChannels(res.data.channels || []);
                                } catch (err) {
                                  setError('Failed to load channels');
                                  setTeamChannels([]);
                                } finally {
                                  setLoadingTeams(false);
                                }
                              } else {
                                setTeamChannels([]);
                              }
                            }}
                            className="flex-1 px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                            data-testid="graph-select-team"
                          >
                            <option value="">Select a team...</option>
                            {availableTeams.map(team => (
                              <option key={team.id} value={team.id}>{team.name}</option>
                            ))}
                          </select>
                          <button
                            onClick={async () => {
                              setLoadingTeams(true);
                              try {
                                const res = await api.listAvailableTeams();
                                setAvailableTeams(res.data.teams || []);
                                if (res.data.teams?.length === 0) {
                                  setError('No Teams found. Ensure the app has Team.ReadBasic.All permission.');
                                }
                              } catch (err) {
                                setError('Failed to fetch Teams: ' + (err.response?.data?.detail || err.message));
                              } finally {
                                setLoadingTeams(false);
                              }
                            }}
                            disabled={loadingTeams}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                            data-testid="refresh-teams-btn"
                          >
                            {loadingTeams ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Select Channel</label>
                        <select
                          value={newGraphTarget.channel_id}
                          onChange={e => {
                            const channel = teamChannels.find(c => c.id === e.target.value);
                            setNewGraphTarget({ 
                              ...newGraphTarget, 
                              channel_id: e.target.value,
                              channel_name: channel?.name || ''
                            });
                          }}
                          disabled={!newGraphTarget.team_id || loadingTeams}
                          className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg disabled:bg-gray-100"
                          data-testid="graph-select-channel"
                        >
                          <option value="">Select a channel...</option>
                          {teamChannels.map(channel => (
                            <option key={channel.id} value={channel.id}>{channel.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* DM User Email */}
                  {newGraphTarget.target_type === 'dm' && (
                    <div className="mb-4">
                      <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">User Email</label>
                      <input
                        type="email"
                        value={newGraphTarget.user_email || ''}
                        onChange={e => setNewGraphTarget({ ...newGraphTarget, user_email: e.target.value })}
                        placeholder="user@company.com"
                        className="w-full md:w-1/2 px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                        data-testid="graph-dm-email"
                      />
                      <p className="text-xs text-[var(--clr-text-muted)] mt-1">
                        Requires Chat.Create and Chat.ReadWrite.All permissions
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!newGraphTarget.label) {
                          setError('Please enter a label');
                          return;
                        }
                        if (newGraphTarget.target_type === 'channel' && (!newGraphTarget.team_id || !newGraphTarget.channel_id)) {
                          setError('Please select a team and channel');
                          return;
                        }
                        if (newGraphTarget.target_type === 'dm' && !newGraphTarget.user_email) {
                          setError('Please enter user email');
                          return;
                        }
                        
                        try {
                          setSaving(true);
                          await api.addTeamsGraphTarget(newGraphTarget);
                          setNewGraphTarget({ team_id: '', channel_id: '', label: '', target_type: 'channel' });
                          const res = await api.getTeamsGraphTargets();
                          setGraphTargets(res.data.targets || []);
                          setSuccess('Target added successfully');
                          setTimeout(() => setSuccess(null), 3000);
                        } catch (err) {
                          setError(err.response?.data?.detail || 'Failed to add target');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                      className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                      data-testid="add-graph-target-btn"
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Add Target
                    </button>
                    
                    <button
                      onClick={async () => {
                        if (newGraphTarget.target_type === 'channel' && (!newGraphTarget.team_id || !newGraphTarget.channel_id)) {
                          setError('Select a team and channel to test');
                          return;
                        }
                        if (newGraphTarget.target_type === 'dm' && !newGraphTarget.user_email) {
                          setError('Enter user email to test');
                          return;
                        }
                        
                        setTestingGraph('new');
                        try {
                          const res = await api.testTeamsGraphMessage(newGraphTarget);
                          if (res.data.success) {
                            setSuccess('Test message sent successfully!');
                          } else {
                            setError('Test failed: ' + (res.data.result?.error || 'Unknown error'));
                          }
                          setTimeout(() => setSuccess(null), 5000);
                        } catch (err) {
                          setError('Test failed: ' + (err.response?.data?.detail || err.message));
                        } finally {
                          setTestingGraph(null);
                        }
                      }}
                      disabled={testingGraph === 'new'}
                      className="px-4 py-2 border border-[var(--clr-border-light)] rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      data-testid="test-graph-target-btn"
                    >
                      {testingGraph === 'new' ? 'Testing...' : 'Test Connection'}
                    </button>
                  </div>
                </div>

                {/* Configured Targets List */}
                {graphTargets.length > 0 && (
                  <div className="border-t border-[var(--clr-border-light)] pt-6">
                    <h4 className="font-medium text-[var(--clr-text-primary)] mb-4">Configured Targets ({graphTargets.length})</h4>
                    <div className="space-y-3">
                      {graphTargets.map((target, idx) => (
                        <div key={target.id || idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              target.target_type === 'dm' ? 'bg-blue-100' : 'bg-purple-100'
                            }`}>
                              {target.target_type === 'dm' ? (
                                <Users className="w-4 h-4 text-blue-600" />
                              ) : (
                                <MessageSquare className="w-4 h-4 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-[var(--clr-text-primary)]">{target.label}</p>
                              <p className="text-xs text-[var(--clr-text-muted)]">
                                {target.target_type === 'dm' 
                                  ? `DM: ${target.user_email}`
                                  : `${target.team_name || 'Team'} → ${target.channel_name || 'Channel'}`
                                }
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={target.enabled}
                                onChange={async (e) => {
                                  try {
                                    await api.updateTeamsGraphTarget(target.id, { enabled: e.target.checked });
                                    const res = await api.getTeamsGraphTargets();
                                    setGraphTargets(res.data.targets || []);
                                  } catch (err) {
                                    setError('Failed to update target');
                                  }
                                }}
                                className="rounded"
                              />
                              Enabled
                            </label>
                            <button
                              onClick={async () => {
                                setTestingGraph(target.id);
                                try {
                                  const testData = target.target_type === 'dm'
                                    ? { user_email: target.user_email }
                                    : { team_id: target.team_id, channel_id: target.channel_id };
                                  const res = await api.testTeamsGraphMessage(testData);
                                  if (res.data.success) {
                                    setSuccess('Test sent!');
                                  } else {
                                    setError('Test failed: ' + (res.data.result?.error || 'Unknown'));
                                  }
                                } catch (err) {
                                  setError('Test failed');
                                } finally {
                                  setTestingGraph(null);
                                }
                              }}
                              disabled={testingGraph === target.id}
                              className="p-2 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-purple-50 rounded"
                              data-testid={`test-target-${idx}`}
                            >
                              {testingGraph === target.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={async () => {
                                if (!confirm('Delete this target?')) return;
                                try {
                                  await api.deleteTeamsGraphTarget(target.id);
                                  const res = await api.getTeamsGraphTargets();
                                  setGraphTargets(res.data.targets || []);
                                  setSuccess('Target deleted');
                                } catch (err) {
                                  setError('Failed to delete');
                                }
                              }}
                              className="p-2 text-red-500 hover:bg-red-50 rounded"
                              data-testid={`delete-target-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Templates Tab Renderer
  function renderTemplatesTab() {
    const availableSections = ['health', 'blockers', 'risks', 'issues', 'dependencies', 'milestones', 'decisions', 'overdue', 'upcoming'];
    
    const handleCloneTemplate = async () => {
      if (!cloneName.trim()) {
        setError('Please enter a name for the cloned template');
        return;
      }
      try {
        setSaving(true);
        await api.cloneSignalTemplate(cloneTarget.id, cloneName);
        setSuccess('Template cloned successfully');
        setCloneModalOpen(false);
        setCloneTarget(null);
        setCloneName('');
        loadData();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to clone template');
      } finally {
        setSaving(false);
      }
    };

    const handlePreviewTemplate = async (template) => {
      try {
        const res = await api.previewSignalTemplate(template.id);
        setTemplatePreview({ template, ...res.data });
      } catch (err) {
        setError('Failed to generate preview');
      }
    };

    const handleDeleteTemplate = async (templateId) => {
      if (!window.confirm('Are you sure you want to delete this template?')) return;
      try {
        setSaving(true);
        await api.deleteSignalTemplate(templateId);
        setSuccess('Template deleted');
        loadData();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete template');
      } finally {
        setSaving(false);
      }
    };

    const handleSaveTemplate = async () => {
      if (!editingTemplate) return;
      try {
        setSaving(true);
        if (editingTemplate.id) {
          await api.updateSignalTemplate(editingTemplate.id, editingTemplate);
        } else {
          await api.createSignalTemplate(editingTemplate);
        }
        setSuccess('Template saved');
        setEditingTemplate(null);
        loadData();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to save template');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="p-6 space-y-6" data-testid="templates-tab">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-[var(--clr-text-primary)]">Daily Signal Templates</h3>
            <p className="text-sm text-[var(--clr-text-muted)]">
              Customize the format and content of Daily Signal notifications
            </p>
          </div>
          <button
            onClick={() => setEditingTemplate({
              name: '',
              description: '',
              channel: 'all',
              sections: ['health', 'blockers', 'risks'],
              format_config: { show_header: true, show_timestamp: true, max_items_per_section: 3, include_emojis: true },
              header_template: '📊 ScopeOps Daily Signal™',
              subtitle_template: '{{date}} — {{scope_name}}',
              footer_template: '',
              is_active: true
            })}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] transition-colors"
            data-testid="create-template-btn"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>

        {/* Clone Modal */}
        {cloneModalOpen && cloneTarget && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Clone Template</h3>
              <p className="text-sm text-[var(--clr-text-muted)] mb-4">
                Create a copy of "{cloneTarget.name}" with a new name
              </p>
              <input
                type="text"
                value={cloneName}
                onChange={e => setCloneName(e.target.value)}
                placeholder="New template name"
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg mb-4"
                data-testid="clone-name-input"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setCloneModalOpen(false); setCloneTarget(null); setCloneName(''); }}
                  className="px-4 py-2 text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCloneTemplate}
                  disabled={saving}
                  className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                  data-testid="confirm-clone-btn"
                >
                  {saving ? 'Cloning...' : 'Clone'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit/Create Modal */}
        {editingTemplate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{editingTemplate.id ? 'Edit Template' : 'Create Template'}</h3>
                <button onClick={() => setEditingTemplate(null)} className="text-[var(--clr-text-muted)] hover:text-[var(--clr-text-primary)]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Name</label>
                  <input
                    type="text"
                    value={editingTemplate.name}
                    onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                    data-testid="template-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Description</label>
                  <input
                    type="text"
                    value={editingTemplate.description || ''}
                    onChange={e => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                    className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                    placeholder="Brief description of this template"
                    data-testid="template-description-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Channel</label>
                  <select
                    value={editingTemplate.channel}
                    onChange={e => setEditingTemplate({ ...editingTemplate, channel: e.target.value })}
                    className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                    data-testid="template-channel-select"
                  >
                    <option value="all">All Channels</option>
                    <option value="email">Email Only</option>
                    <option value="whatsapp">WhatsApp Only</option>
                    <option value="teams">Teams Only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">Sections</label>
                  <div className="flex flex-wrap gap-2">
                    {availableSections.map(section => (
                      <button
                        key={section}
                        onClick={() => {
                          const sections = editingTemplate.sections || [];
                          if (sections.includes(section)) {
                            setEditingTemplate({ ...editingTemplate, sections: sections.filter(s => s !== section) });
                          } else {
                            setEditingTemplate({ ...editingTemplate, sections: [...sections, section] });
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          (editingTemplate.sections || []).includes(section)
                            ? 'bg-[var(--clr-brand-primary)] text-white'
                            : 'bg-gray-100 text-[var(--clr-text-secondary)] hover:bg-gray-200'
                        }`}
                        data-testid={`section-toggle-${section}`}
                      >
                        {section.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Header Template</label>
                  <input
                    type="text"
                    value={editingTemplate.header_template}
                    onChange={e => setEditingTemplate({ ...editingTemplate, header_template: e.target.value })}
                    className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                    placeholder="📊 Daily Signal™"
                    data-testid="template-header-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Subtitle Template</label>
                  <input
                    type="text"
                    value={editingTemplate.subtitle_template}
                    onChange={e => setEditingTemplate({ ...editingTemplate, subtitle_template: e.target.value })}
                    className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                    placeholder="Available: {{date}}, {{scope_name}}, {{user_name}}"
                    data-testid="template-subtitle-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">Format Options</label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editingTemplate.format_config?.show_header ?? true}
                        onChange={e => setEditingTemplate({
                          ...editingTemplate,
                          format_config: { ...editingTemplate.format_config, show_header: e.target.checked }
                        })}
                        className="rounded"
                      />
                      Show header
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editingTemplate.format_config?.show_timestamp ?? true}
                        onChange={e => setEditingTemplate({
                          ...editingTemplate,
                          format_config: { ...editingTemplate.format_config, show_timestamp: e.target.checked }
                        })}
                        className="rounded"
                      />
                      Show timestamp
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editingTemplate.format_config?.include_emojis ?? true}
                        onChange={e => setEditingTemplate({
                          ...editingTemplate,
                          format_config: { ...editingTemplate.format_config, include_emojis: e.target.checked }
                        })}
                        className="rounded"
                      />
                      Include emojis
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editingTemplate.format_config?.show_cta_buttons ?? true}
                        onChange={e => setEditingTemplate({
                          ...editingTemplate,
                          format_config: { ...editingTemplate.format_config, show_cta_buttons: e.target.checked }
                        })}
                        className="rounded"
                      />
                      Show CTA buttons
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Max Items Per Section</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={editingTemplate.format_config?.max_items_per_section ?? 3}
                    onChange={e => setEditingTemplate({
                      ...editingTemplate,
                      format_config: { ...editingTemplate.format_config, max_items_per_section: parseInt(e.target.value) }
                    })}
                    className="w-24 px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                    data-testid="template-max-items-input"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editingTemplate.is_active}
                    onChange={e => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                    className="rounded"
                  />
                  Template is active
                </label>
              </div>

              <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[var(--clr-border-light)]">
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={saving || !editingTemplate.name}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                  data-testid="save-template-btn"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {templatePreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Template Preview: {templatePreview.template.name}</h3>
                <button onClick={() => setTemplatePreview(null)} className="text-[var(--clr-text-muted)] hover:text-[var(--clr-text-primary)]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="text-center border-b border-gray-200 pb-4">
                  <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">{templatePreview.preview.header}</h2>
                  <p className="text-sm text-[var(--clr-text-muted)]">{templatePreview.preview.subtitle}</p>
                </div>

                {templatePreview.preview.sections.map((section, idx) => (
                  <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                    <h4 className="font-semibold text-[var(--clr-text-primary)] mb-2 capitalize">{section.title}</h4>
                    <ul className="space-y-1">
                      {section.items.slice(0, 3).map((item, i) => (
                        <li key={i} className="text-sm text-[var(--clr-text-secondary)]">
                          • {item.title || item.name || JSON.stringify(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {templatePreview.preview.footer && (
                  <p className="text-xs text-[var(--clr-text-muted)] text-center pt-4">{templatePreview.preview.footer}</p>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setTemplatePreview(null)}
                  className="px-4 py-2 bg-gray-100 text-[var(--clr-text-secondary)] rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(template => (
            <div
              key={template.id}
              className={`border rounded-xl p-5 transition-all ${
                template.is_system
                  ? 'bg-blue-50/50 border-blue-200'
                  : 'bg-white border-[var(--clr-border-light)] hover:border-[var(--clr-brand-primary)]'
              }`}
              data-testid={`template-card-${template.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-[var(--clr-text-primary)]">{template.name}</h4>
                    {template.is_system && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">System</span>
                    )}
                    {!template.is_active && !template.is_system && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--clr-text-muted)]">{template.description}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {(template.sections || []).slice(0, 5).map(section => (
                  <span key={section} className="text-xs bg-gray-100 text-[var(--clr-text-secondary)] px-2 py-0.5 rounded">
                    {section}
                  </span>
                ))}
                {(template.sections || []).length > 5 && (
                  <span className="text-xs text-[var(--clr-text-muted)]">+{template.sections.length - 5} more</span>
                )}
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-[var(--clr-border-light)]">
                <button
                  onClick={() => handlePreviewTemplate(template)}
                  className="flex items-center gap-1 text-sm text-[var(--clr-text-secondary)] hover:text-[var(--clr-brand-primary)]"
                  data-testid={`preview-${template.id}`}
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
                <button
                  onClick={() => { setCloneTarget(template); setCloneModalOpen(true); }}
                  className="flex items-center gap-1 text-sm text-[var(--clr-text-secondary)] hover:text-[var(--clr-brand-primary)]"
                  data-testid={`clone-${template.id}`}
                >
                  <Copy className="w-4 h-4" />
                  Clone
                </button>
                {!template.is_system && (
                  <>
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="flex items-center gap-1 text-sm text-[var(--clr-text-secondary)] hover:text-[var(--clr-brand-primary)]"
                      data-testid={`edit-${template.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700"
                      data-testid={`delete-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-12 text-[var(--clr-text-muted)]">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No templates found</p>
          </div>
        )}
      </div>
    );
  }
};

// Role Default Editor Component
const RoleDefaultEditor = ({ data, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState(data);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Frequency</label>
          <select
            value={formData.frequency}
            onChange={(e) => setFormData({...formData, frequency: e.target.value})}
            className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg"
          >
            <option value="daily">Daily</option>
            <option value="weekdays">Weekdays</option>
            <option value="weekly">Weekly</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Max per Day</label>
          <input
            type="number"
            value={formData.max_per_day}
            onChange={(e) => setFormData({...formData, max_per_day: parseInt(e.target.value)})}
            min="1"
            max="10"
            className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Default Categories</label>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => {
                const cats = formData.categories || [];
                if (cats.includes(cat)) {
                  setFormData({...formData, categories: cats.filter(c => c !== cat)});
                } else {
                  setFormData({...formData, categories: [...cats, cat]});
                }
              }}
              className={`px-2 py-1 text-xs rounded border ${
                (formData.categories || []).includes(cat)
                  ? 'bg-[var(--clr-brand-primary)] text-white border-[var(--clr-brand-primary)]'
                  : 'border-[var(--clr-border-medium)] text-[var(--clr-text-secondary)]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.allow_custom_times}
            onChange={(e) => setFormData({...formData, allow_custom_times: e.target.checked})}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-[var(--clr-text-primary)]">Allow custom times</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.allow_detailed}
            onChange={(e) => setFormData({...formData, allow_detailed: e.target.checked})}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-[var(--clr-text-primary)]">Allow detailed format</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.include_cost_fields}
            onChange={(e) => setFormData({...formData, include_cost_fields: e.target.checked})}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-[var(--clr-text-primary)]">Include cost fields</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(formData)}
          disabled={saving}
          className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Defaults'}
        </button>
      </div>
    </div>
  );
};

// User Override Editor Component
const UserOverrideEditor = ({ data, users, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState(data);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-[var(--clr-border-light)]">
          <h3 className="text-lg font-medium text-[var(--clr-text-primary)]">
            {data.user_id ? 'Edit Override' : 'Add Override'}
          </h3>
        </div>
        <div className="p-4 space-y-4">
          {!data.user_id && (
            <div>
              <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">User</label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({...formData, user_id: e.target.value})}
                className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg"
              >
                <option value="">Select user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Reason</label>
            <textarea
              value={formData.reason || ''}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Reason for this override..."
              className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm text-[var(--clr-text-secondary)] mb-1">Expires At</label>
            <input
              type="date"
              value={formData.expires_at ? new Date(formData.expires_at).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({...formData, expires_at: e.target.value ? new Date(e.target.value) : null})}
              className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allow_custom_times ?? false}
                onChange={(e) => setFormData({...formData, allow_custom_times: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-[var(--clr-text-primary)]">Custom times</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allow_detailed ?? false}
                onChange={(e) => setFormData({...formData, allow_detailed: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-[var(--clr-text-primary)]">Detailed format</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.include_cost_fields ?? false}
                onChange={(e) => setFormData({...formData, include_cost_fields: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-[var(--clr-text-primary)]">Cost fields</span>
            </label>
          </div>
        </div>
        <div className="p-4 border-t border-[var(--clr-border-light)] flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={saving || !formData.user_id}
            className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Override'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationConsole;
